import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import CustomerDropDown from '../../components/shared/CustomerDropDown'
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import RatesDropDown from '../../components/shared/RatesDropDown'
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import { useMessageResources, useTextResources } from '../../language';
import { Form, Popup } from 'semantic-ui-react';
import GridSearchTemplate from '../../pages/GridSearchTemplate';
import { AllocationAndStrategy } from '../../api/services/next'
import CheckboxWrapper from '../../components/shared/Checkbox';
import { useLoader } from '../../hooks/useLoader';
import { onError, onSuccess } from '../../components/shared/messages';
import { StrategyCreationPopup } from '../../components/AllocationAndStrategy/StrategyCreationPopup';
import { AllocationCreationForStrategyPopup } from '../../components/AllocationAndStrategy/AllocationCreationForStrategyPopup';
import { useConfirm } from '../../hooks/useConfirm';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import moment from 'moment';
import { DatePicker } from '../../components/shared/DatePicker'
import { dateFormat } from "../../components/shared/constants";
import { SyncChannelManagerProducts } from '../../api/services/pms';
import './style.css';
import { toServerDateFormat } from '../../components/shared/utils';

const Strategy = ({ HotelId = useCurrentHotel()?.Id }) => {
    const confirm  = useConfirm();
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const [data, setData] = useState([]);
    const [hotelIds, setHotelIds] = useState(HotelId ? [HotelId] : []);
    const [spec, setSpec] = useState([]);
    const [showAllocationCreationPopup, setShowAllocationCreationPopup] = useState(false);
    const [selectedStrategy, setSelectedStrategy] = useState({});

    const RatesSpec = useMemo(() => {
        let spec = {
            HotelIds: hotelIds,
            IncludeDayUse: true,
            IncludeRatePricingType: true,
            IsActive: true,
        }
        return spec;
    }, [hotelIds]);

    const RoomTypesSpec = useMemo(() => {
        let spec = {
            HotelIds: hotelIds,
        }
        return spec;
    }, [hotelIds]);

    const formatter = ({ value }) => {
        return value || ""
    }

    const addRow = () => {
        return sendRequest(null, null, true);
    }

    const sendRequest = async (callback, dataArr, refreshData, isDelete) => {
        try {
            loader(true)
            if (callback) {
                await callback(dataArr);
                onSuccess(isDelete ? "DeletedSuccessfully" : null);
            }
            if (refreshData) {
                var res = await AllocationAndStrategy.GetStrategies(spec);
                res = res.map(s => ({ ...s, Actions: s.StrategyId, DatesRange: s.StartDate && s.EndDate ? `${dateFormatter(s.StartDate)} - ${dateFormatter(s.EndDate)}` : "" }));
                setData(res);
            }
        } catch (error) {
            onError(message(error?.error?.detail));
        } finally {
            loader(false);
        }
    }

    const onSearchChange = (spec) => {
        spec.FromDate = spec["fromToDate[0]"] ? spec["fromToDate[0]"] : null;
        spec.ToDate = spec["fromToDate[1]"] ? spec["fromToDate[1]"] : null;
        setSpec(spec)
    }

    const actionsFormatter = ({ value, row }) => {
        const strategy = value && data.length && data.find(s => s.StrategyId === value);
        if (row) {
            return row.AllocationId && row.AllocationIsActive ? <a href='#' onClick={() => { sendDelete(row.AllocationId, false, row) }}>{`${text("Delete")} ${text("Allocation")}`} </a> : " "
        }
        else {
            return strategy.IsActive ? <span>
                <a href='#' onClick={() => { sendDelete(value, true, strategy) }}>{text("Delete")} </a> <br />
                <a href='#' onClick={() => { return openAllocationCreationPopup(strategy) }}>{`${text("Add")} ${text("Allocation")}`} </a>
            </span> : " "
        }
    };

    const tooltipFormatter = ({ value }) => {
        return value ? <span>
            <Popup size='small' content={value} trigger={<span>{value} </span>} />
        </span> : ""
    };

    const idFormatter = ({ value }) => {
        const selectedStrategy = value && data.length && data.find(a => a.StrategyId == value);
        return selectedStrategy ?
            <span className={selectedStrategy.IsActive ? "" : "inActive"}>{selectedStrategy?.StrategyName} </span>
            : ""
    };

    const nameFormatter = (value) => {
        if (value.row) {
            return <Popup size='small' content={IdTooltip(value.row.AllocationId)} trigger={<span>{value.row.AllocationName} </span>} />

        }
        else {
            const allocationId = data.find(a => a.AllocationName == value?.value )?.AllocationId;
            return <Popup size='small' content={IdTooltip(allocationId)} trigger={<span>{value?.value} </span>} />
        }
    }

    const IdTooltip = (Id) => {
        return (
            <p style={{ padding: '5px' }}>
                <div>{`${text('Identifier')}: ${Id}`}</div>
            </p>
        );
    }

    const dateFormatter = (value) => {
        return value ? moment(value).format(dateFormat) : "";
    };

    const sendDelete = async (id, isDeleteStrategy, strategyAllocation) => {
        await confirm({
            header: `${text('Delete')} ${text(isDeleteStrategy ? 'Strategy' : 'Allocation')}`,
            confirmButton: {
                content: 'Yes',
                positive: true,
            },
            cancelButton: {
                content: 'No',
                negative: true,
            },
            onConfirm: async () => {

                await sendRequest(isDeleteStrategy ? AllocationAndStrategy.DeleteStrategy : AllocationAndStrategy.DeleteAllocations, [id], true, true);

                if (strategyAllocation) {
                    const roomTypeIds = isDeleteStrategy ? data.filter(s => s.StrategyId == strategyAllocation.StrategyId)
                        .map(s => s.RoomTypeId) : [strategyAllocation.RoomTypeId];

                    const rateIds = isDeleteStrategy ? data.filter(s => s.StrategyId == strategyAllocation.StrategyId)
                        .map(s => s.RateId) : [strategyAllocation.RateId];

                    const minDate = isDeleteStrategy ? strategyAllocation.StrategyMinDate : strategyAllocation.MinDate;
                    const maxDate = isDeleteStrategy ? strategyAllocation.StrategyMaxDate : strategyAllocation.MaxDate;

                    if (minDate && maxDate) {
                        try {
                            loader(true);
                            await SyncChannelManagerProducts({
                                HotelID: strategyAllocation.HotelId,
                                RateIds: [...new Set(rateIds)],
                                RoomTypeIds: [...new Set(roomTypeIds)],
                                StartDate: toServerDateFormat(minDate),
                                EndDate: toServerDateFormat(maxDate),
                                IsUpdateAvailabilty: true,
                                RecalculateAvailabilty: true,
                                Reason: isDeleteStrategy ? `Strategy deleted, Strategy ID: ${id}` : `Strategy allocation deleted, Allocation ID: ${id}`,
                                TriggerOperation: isDeleteStrategy ? "Strategy Delete" : "Strategy Allocation Delete"
                            })
                        } catch (error) {
                            onError(message(error));
                        } finally {
                            loader(false);
                        }
                    }
                }
            },
            onCancel: () =>
                sendRequest(null, null, true)
        });
    }

    const openAllocationCreationPopup = (strategy) => {
        setSelectedStrategy(strategy);
        setShowAllocationCreationPopup(true);
        return true;
    }

    const onSearchComplete = (data) => {
        for (let item of data) {
            item.DatesRange = item.StartDate && item.EndDate ? `${dateFormatter(item.StartDate)} - ${dateFormatter(item.EndDate)}` : "";
            item.Actions = item.StrategyId;
        }
        setData(data);
    }

    const gridOptions = {
        search: {
            fields: [
                <Form.Field
                    control={HotelsDropDown}
                    multiple
                    clearable
                    search
                    placeholder={text("All")}
                    label={text("Hotels")}
                    key="HotelIds"
                    onChange={setHotelIds}
                />,
                <Form.Field
                    control={RoomTypesDropDown}
                    multiple
                    clearable
                    search
                    placeholder={text("All")}
                    label={text("RoomTypes")}
                    key="RoomTypeIds"
                    spec={RoomTypesSpec}
                />,
                <Form.Field
                    control={CustomerDropDown}
                    multiple
                    clearable
                    placeholder={text("All")}
                    label={text("Customers")}
                    key="CustomerIds"
                />,
                <Form.Field
                    control={RatesDropDown}
                    multiple
                    clearable
                    search
                    placeholder={text("All")}
                    spec={RatesSpec}
                    label={text("Rates")}
                    key="RateIds"
                />,
                <DatePicker
                    key="fromToDate"
                    picktype='range'
                    label={text("DatesRange")}
                    clearable
                />,
                <CheckboxWrapper
                    key="InActive"
                    label={text("IncludeInactive")}
                />
            ],

            method: 'GET',
            api: AllocationAndStrategy.GetStrategies,
            searchOnLoad: true,
            initial: hotelIds.length
                ? {
                    HotelIds: hotelIds,
                    InActive: false
                }
                : null
        },
        grid: {
            columns: [
                { name: 'StrategyId', title: text('Strategy'), formatter: idFormatter },
                { name: 'AllocationName', title: text('Allocation'), formatter: nameFormatter },
                { name: 'HotelName', title: text('Hotel'), formatter },
                { name: 'WingName', title: text('Wing'), formatter },
                { name: 'RoomTypeName', title: text('RoomType'), formatter },
                { name: 'RateName', title: text('Rate'), formatter },
                { name: 'CustomerNames', title: text('CustomerName'), formatter: tooltipFormatter },
                { name: 'Priority', title: text('Priority'), formatter },
                { name: 'AllocatedRooms', title: text('NumberAllocatedRooms'), formatter },
                { name: 'UsedRooms', title: text('UsedRooms'), formatter },
                { name: 'DatesRange', title: text('DatesRange'), formatter },
                { name: 'Actions', title: text('Actions'), formatter: actionsFormatter },
            ],
            cacheId: `Strategy`,
            customOptions: {
                sort: {
                    sorting: [
                        { columnName: 'Priority', direction: 'desc' }
                    ]
                },
                group: {
                    grouping: [{ columnName: 'StrategyId' }],
                    extensions: [{ columnName: 'StrategyId', groupingEnabled: false }],
                },
            },
            filtering: {
                extensions: [
                    { columnName: 'Actions', filteringEnabled: false }
                ]
            },
            showFilter: true,
            summary: true,
            groupSummaryItems: [
                { columnName: 'Actions', type: 'max', alignByColumn: true, formatter: actionsFormatter, hideTypeName: true }
            ],
            noVirtualTable: true,
            paging: true,
            rowClassCallback: a => (a.AllocationId && !a.AllocationIsActive) || !a.IsActive ? "inActive" : "",
            editable: {
                allowAdd: true,
                modalEditor: {
                    modal: StrategyCreationPopup,
                    props: {
                        addRow: addRow
                    }
                }
            }
        },
    };
    return (
        <div className="gridSearchTemplate">
            <GridSearchTemplate options={gridOptions} onSearchChange={onSearchChange} data={data} onSearchComplete={onSearchComplete} />
            <AllocationCreationForStrategyPopup
                open={showAllocationCreationPopup}
                onClose={() => setShowAllocationCreationPopup(false)}
                strategy={selectedStrategy}
                refreshData={() => sendRequest(undefined, undefined, true)}
            />
        </div>
    )
};

Strategy.propTypes = {
    HotelId: PropTypes.any
};

export default Strategy;
